import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import {
    IconButton,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Divider,
    Rating
} from '@mui/material';
import { MoreVert, ExpandMore } from '@mui/icons-material';
import SearchFilter from "./SearchFilter"
import Menus from "../../../../../components/Dialog/Menus";
import DrawerOverlay from '../../../../../components/Drawer/DrawerOverlay';
import PendingReviewCard from './PendingReviewCard';
import EventReview from './EventReview';
import InvoicesModal from './InvoicesModal';
import NoCreatedEvent from '../../my-events/NoCreatedEvent';
import LoadingSpinner from '../../../../../animation/LoadingSpinner';
import useEventBookings from './hooks/useEventBookings';
import { ccyFormat } from '../../../../../utils/currencyFormatter';
import { EVENT_STATUS, EVENT_TYPES } from '../../../../../hooks/event-hooks/useEvent';
import { formatEventDate } from "../../../../../utils/dateFormatter";
import { H4, Body2, Body3, Body1 } from "../../../../../themes/typography";
import "../../../../../styles/user-dashboard/MyEvents.css"

export default function EventBookings() {

    const {
        filterByStatus,
        anchorEl,
        open,
        onElipsisMenuClose,
        onElipsisMenuOpen,
        setSearchTerm,
        setSelectedFilter,
        menusList,
        filteredMyEventBookings,
        activeTab,
        setActiveTab,
        openPendingRatingsDrawer,
        setOpenPendingRatingsDrawer,
        onExploreEventsClick,
        onGiveFeedbackClick,
        openEventReviewModal,
        onInvoiceDownload,
        openInvoiceModal,
        invoiceModalType,
        onInvoiceModalClose,
        onViewTicketClick,
        loading
    } = useEventBookings();

    if (loading) {
        return <LoadingSpinner message='Loading your event bookings' />;
    }

    if (filteredMyEventBookings.length === 0 && !loading) {
        return (
            <Fragment>
                <NoCreatedEvent
                    title='No event bookings found'
                    description='You have not booked any events yet. Explore and book events to see them listed here.'
                    btnName='Explore Events'
                    onClick={onExploreEventsClick}
                />
            </Fragment>
        )
    }

    // console.log("Filtered Events -> ", filteredMyEventBookings)
    return (
        <Fragment>
            <div id="my-events-container">
                <div className="my-event-list-wrapper-container">

                    <div className="my-event-list-sort-search-container">
                        <SearchFilter
                            eventStatusOptions={filterByStatus}
                            onSearchInputChange={(value) => setSearchTerm(value)}
                            onFilterChange={(value) => setSelectedFilter(value)}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            onShareFeedbackClick={() => setOpenPendingRatingsDrawer(true)}
                        />
                    </div>

                    <div className="my-event-list">
                        <MyEventBooking
                            menusList={menusList}
                            open={open}
                            anchorEl={anchorEl}
                            onElipsisMenuClose={onElipsisMenuClose}
                            onElipsisMenuOpen={onElipsisMenuOpen}
                            onViewTicketClick={onViewTicketClick}
                        />
                        {/* {filteredMyEventBookings.map((booking) => (
                            <MyEventBooking
                                key={booking._id}
                                menusList={menusList}
                                open={open}
                                anchorEl={anchorEl}
                                onElipsisMenuClose={onElipsisMenuClose}
                                onElipsisMenuOpen={onElipsisMenuOpen}
                                onViewTicketClick={onViewTicketClick}
                            />
                        ))} */}
                    </div>
                </div>
            </div>
            <DrawerOverlay
                open={openPendingRatingsDrawer}
                onClose={() => setOpenPendingRatingsDrawer(false)}
                drawerTitle="Pending Event Ratings"
                shouldShowPrimaryButton={false}
                secondaryButtonText='Maybe Later'
            >
                <div className="pending-event-reviews-drawer-content">
                    {Array.from({ length: 4 }).map((_, index) => (
                        <PendingReviewCard key={index} />
                    ))}
                </div>
            </DrawerOverlay>

            <EventReview
                open={openEventReviewModal}
                onClose={onGiveFeedbackClick}
                onSubmit={onGiveFeedbackClick}
            />

            <InvoicesModal
                open={openInvoiceModal}
                modalType={invoiceModalType}
                onDownload={onInvoiceDownload}
                onClose={onInvoiceModalClose}
            />
        </Fragment>
    )
}

const eventImg =
    'https://images.unsplash.com/photo-1719937051176-9b98352a6cf4?w=600&amp;auto=format&amp;fit=crop&amp;q=60&amp;ixlib=rb-4.0.3&amp;ixid=M3wxMjA3fDF8MHxmZWF0dXJlZC1waG90b3MtZmVlZHwxfHx8ZW58MHx8fHx8';

function MyEventBooking({
    name = 'AI Innovations and Future',
    venueName = 'Tale Crafted Events, Surat',
    latitude = 21.1754615,
    longitude = 72.7830602,
    eventThumbnail = eventImg,
    status,
    startDate = '2025-09-12',
    startTime = '10:00 AM',
    endDate = 'Mon Sep 25, 2023',
    endTime = '5:00 PM',
    eventType = 'single_day',
    schedule = [],
    slug,
    isPublishable = {},
    createdAt,
    updatedAt,
    bookingId = 'BKID1234567890',
    amountPaid = 1500,
    overallRatings = 3.5,
    ticketBooked = 3,
    bookedBy = 'John Doe',
    bookedOn = 'Sep 01, 2024 at 02:30 PM',
    onElipsisMenuOpen = () => { },
    onElipsisMenuClose = () => { },
    open,
    anchorEl,
    menusList = [],
    paymentStatus = 'ticketBooked',
    onViewTicketClick = () => { },
}) {

    function getEventTypeText(eventType) {
        switch (eventType) {
            case 'multi_day':
                return 'Multi Date';
            case 'continuous':
                return 'Continuous';
            default:
                return 'Single Date';
        }
    }

    /** TODO:
     * started on -> use closest booking ticket schedule date if it crossed current date
     * starting on -> use closest booking tickets schedule
     * cancelled on -> use last event updated date
     * ended on -> use event end date
     * remove drafted on, created on
     */
    function changeStartingOnText(status) {
        if (status === EVENT_STATUS.live) {
            return 'Ending on :';
        } else if (status === EVENT_STATUS.ended) {
            return 'Ended on :';
        } else if (status === EVENT_STATUS.cancelled) {
            return 'Cancelled on :';
        } else if (status === EVENT_STATUS.drafted && !isPublishable?.schedule) {
            return 'Created on :';
        } else if (status === EVENT_STATUS.drafted && isPublishable?.schedule) {
            return 'Drafted on :';
        } else {
            return 'Starting on :';
        }
    }

    function AddEventScheduleChip() {
        // Check if it's a single day event
        if (eventType === EVENT_TYPES.singleDay) {
            // Check if schedule exists and has at least one entry with slots
            if (
                schedule &&
                schedule.length > 0 &&
                schedule[0]?.slots &&
                schedule[0]?.slots.length > 0
            ) {
                const slotsCount = schedule[0]?.slots?.length;
                return `${slotsCount} slots`;
            }
        }
        // Check if it's a multi-day event
        else if (eventType === 'multi_day') {
            // Check if schedule exists
            if (schedule && schedule.length > 0) {
                // Count total slots across all schedule dates
                let totalSlots = 0;

                // Loop through each schedule day and count slots
                schedule.forEach((day) => {
                    if (day.slots && day.slots.length > 0) {
                        totalSlots += day.slots.length;
                    }
                });

                // Only return if there are slots
                if (totalSlots > 0) {
                    return `${totalSlots} slots`;
                }
            }
        }
        return null;
    }

    const addEventScheduleChip = AddEventScheduleChip();

    function getEventDate() {
        if (EVENT_STATUS.live === status) {
            return `${formatEventDate(startDate)} at ${startTime}`;
        } else if (EVENT_STATUS.ended === status) {
            return `${formatEventDate(endDate)} at ${endTime}`;
        } else if (EVENT_STATUS.cancelled === status) {
            return `${formatEventDate(endDate)} at ${endTime}`;
        } else if (status === EVENT_STATUS.drafted && !isPublishable?.schedule) {
            const createdDate = new Date(createdAt);
            const formattedDate = createdDate.toLocaleDateString('en-US', {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
            const formattedTime = createdDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return `${formattedDate} at ${formattedTime}`;
        } else if (status === EVENT_STATUS.drafted && isPublishable?.schedule) {
            const updatedDate = new Date(updatedAt);
            const formattedDate = updatedDate.toLocaleDateString('en-US', {
                weekday: 'short',
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
            const formattedTime = updatedDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            return `${formattedDate} at ${formattedTime}`;
        } else {
            return `${formatEventDate(startDate)} at ${startTime}`;
        }
    }
    const eventDate = getEventDate();

    return (
        <div id="my-event-list-card-container">
            <div className="event-list-card-main">
                <div className="event-list-card-main-wrapper">
                    <div className="event-list-card-image">
                        <img src={eventThumbnail} alt="Event" />
                    </div>

                    <div className="main-event-list-card-details">
                        <div className="event-list-card-name-and-status">
                            <Link
                                to={`/e/${slug}`}
                                className="event-list-card-name-link"
                            >
                                <H4 fontWeight={600} color="var(--secondary-color)">
                                    {name}
                                </H4>
                            </Link>
                            <div>
                                {paymentStatus === 'refunded' && (
                                    <Chip
                                        color="warning"
                                        label={
                                            <Body3
                                                color="var(--secondary-color)"
                                                fontWeight={400}
                                            >
                                                Refunded
                                            </Body3>
                                        }
                                    />
                                )}

                                {paymentStatus === 'ticketBooked' && (
                                    <Chip
                                        color="success"
                                        label={
                                            <Body3
                                                color="var(--success-secondary-color)"
                                                fontWeight={400}
                                            >
                                                Ticket Booked
                                            </Body3>
                                        }
                                    />
                                )}

                                {paymentStatus === 'refundInitiated' && (
                                    <Chip
                                        color="warning"
                                        label={
                                            <Body3
                                                color="var(--secondary-color)"
                                                fontWeight={400}
                                            >
                                                Refunded Initiated
                                            </Body3>
                                        }
                                    />
                                )}
                            </div>
                        </div>

                        <div className="event-list-card-main-detail-container">
                            <div className="event-list-card-details-container">
                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>Venue :</Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        {venueName ? (
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
                                                className="event-list-card-location-link"
                                                target="_blank"
                                                rel='noopener noreferrer'
                                            >
                                                {venueName}
                                            </a>
                                        ) : (
                                            <Body2 fontWeight={400}>
                                                To be announced
                                            </Body2>
                                        )}
                                    </div>
                                </div>

                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>Event By : </Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        <Body2 fontWeight={400}>
                                            Tale Crafted Events
                                        </Body2>
                                    </div>
                                </div>
                            </div>

                            <div className="event-list-card-details-container">
                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>Booking Id :</Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        <Body2 fontWeight={400}>
                                            {bookingId}
                                        </Body2>
                                    </div>
                                </div>

                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>
                                            Amount Paid :
                                        </Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        <Body2 fontWeight={400}>
                                            {ccyFormat(amountPaid)}
                                        </Body2>
                                    </div>
                                </div>
                            </div>

                            <div className="event-list-card-details-container">

                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>{changeStartingOnText(status)}</Body2>
                                    </div>
                                    <div className="event-list-card-location-value">
                                        <Body2 fontWeight={400}>{eventDate}</Body2>
                                    </div>
                                </div>

                                <div className="event-list-card-location-detail">
                                    <div className="event-list-card-location-label">
                                        <Body2>
                                            Ratings :
                                        </Body2>
                                    </div>

                                    <div className="event-booking-overall-ratings">
                                        <Body2 fontWeight={400}>
                                            {overallRatings}
                                        </Body2>
                                        <Rating
                                            name="overall-ratings-read-only"
                                            value={overallRatings}
                                            readOnly
                                            precision={0.25}
                                            sx={{
                                                color: 'var(--secondary-color)',
                                                '& .MuiRating-icon': {
                                                    color: 'var(--secondary-color)'
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="event-list-card-details-container">
                                <div className="event-list-card-location-detail">

                                    <Chip
                                        color="normal"
                                        label={
                                            <Body3
                                                color="var(--tertiary-color)"
                                                fontWeight={400}
                                            >
                                                {getEventTypeText(eventType)}
                                            </Body3>
                                        }
                                    />

                                    {addEventScheduleChip && (
                                        <Chip
                                            color="normal"
                                            label={
                                                <Body3
                                                    color="var(--tertiary-color)"
                                                    fontWeight={400}
                                                >
                                                    {addEventScheduleChip}
                                                </Body3>
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="event-list-card-elipsis">
                            <IconButton
                                onClick={onElipsisMenuOpen}
                                aria-controls={open ? 'event-booking-menus' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                            >
                                <MoreVert sx={{ fontSize: '1.75rem' }} />
                            </IconButton>
                            <Menus
                                id="event-booking-menus"
                                open={open}
                                anchorEl={anchorEl}
                                onClick={onElipsisMenuClose}
                                onClose={onElipsisMenuClose}
                                menus={menusList}
                                shouldShowIcons={false}
                            />
                        </div>
                    </div>
                </div>
            </div>


            <Accordion
                sx={{
                    width: '100%',
                    background: 'var(--gradient-overlay-bg-color)',
                    '&.MuiAccordion-root.Mui-expanded': {
                        margin: 0
                    }
                }}
            >
                <AccordionSummary
                    expandIcon={
                        <ExpandMore
                            sx={{
                                fontSize: '2rem',
                                color: 'var(--quaternary-color)'
                            }}
                        />
                    }
                >
                    <div className="additional-tray-wrapper">
                        <div className="additional-tray-content">

                            <div className='each-additional-info-item'>
                                <div className="each-additional-info-item-title">
                                    <Body1 fontWeight={350}>
                                        Tickets Booked :
                                    </Body1>
                                </div>
                                <div className="each-additional-info-item-value">
                                    <Body1
                                        color="var(--secondary-color)"
                                        fontWeight={350}
                                    >
                                        {ticketBooked}
                                    </Body1>
                                </div>
                            </div>

                            <div className='each-additional-info-item'>
                                <div className="each-additional-info-item-title">
                                    <Body1 fontWeight={350}>
                                        Booked By :
                                    </Body1>
                                </div>
                                <div className="each-additional-info-item-value">
                                    <Body1
                                        color="var(--secondary-color)"
                                        fontWeight={350}
                                    >
                                        {bookedBy}
                                    </Body1>
                                </div>
                            </div>

                            <div className='each-additional-info-item'>
                                <div className="each-additional-info-item-title">
                                    <Body1 fontWeight={350}>
                                        Booked on :
                                    </Body1>
                                </div>
                                <div className="each-additional-info-item-value">
                                    <Body1
                                        color="var(--secondary-color)"
                                        fontWeight={350}
                                    >
                                        {bookedOn}
                                    </Body1>
                                </div>
                            </div>

                        </div>
                    </div>
                </AccordionSummary>

                <AccordionDetails>

                    <div className='event-booking-attendees-list'>
                        <EachAttendeeRecord
                            onViewTicketClick={onViewTicketClick}
                        />
                        <Divider sx={{ borderColor: 'var(--divider-color)', alignSelf: 'stretch' }} />
                    </div>

                </AccordionDetails>

            </Accordion>

        </div>
    );
}

function EachAttendeeRecord({
    name = 'Jane Smith',
    email = 'janedoe@example.com',
    contactNumber = '9876543210',
    ticketId = 'TIX47896543210',
    onViewTicketClick = () => { }
}) {
    return (
        <div className='each-booking-attendee-record'>

            <div className='each-booking-attendee-details'>
                <div className="each-booking-attendee-name">
                    <Body2>
                        {name}
                    </Body2>
                </div>
                <div className="each-booking-attendee-detail">
                    <div className='attendee-booking-detail'>
                        <Body3>
                            {email}
                        </Body3>

                        <Body3>
                            {contactNumber}
                        </Body3>
                    </div>
                </div>
            </div>

            <div className='each-additional-info-item'>
                <div className="each-additional-info-item-title">
                    <Body2>
                        Ticket Id :
                    </Body2>
                </div>
                <div className="each-additional-info-item-value">
                    <Body2>
                        {ticketId}
                    </Body2>
                </div>
            </div>

            <div className='each-booking-attendee-action-btn'>
                <Button
                    variant="mainSm"
                    onClick={onViewTicketClick}
                >
                    View Ticket
                </Button>
            </div>

        </div>
    )
}